"use client";

import { useForm } from '@/app/FormContext';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import OnboardingProgress from '../onboarding-progress';
import { useEffect, useMemo, useState } from 'react';

type Locale = "es" | "en";

type ServiceOption = {
  optionId: string;
  serviceId: string;
  canonicalServiceId: string;
  selectionLabel: string;
  description: string;
  category: string;
  aliases: string[];
  searchTokens: string[];
};

type ServiceOptionsCategory = {
  category: string;
  total: number;
  options: ServiceOption[];
};

type ServiceOptionsResponse = {
  locale: Locale;
  source: "database" | "fallback";
  total: number;
  categories: ServiceOptionsCategory[];
};

function localizePath(path: string, locale: Locale): string {
  if (locale !== "en") return path;
  if (path.startsWith("/en/") || path === "/en") return path;
  if (path.startsWith("/")) return `/en${path}`;
  return `/en/${path}`;
}

const ES_COPY = {
  title: "Elige tu Trámite",
  visadoTitle: "Elige tu Visado",
  subtitle: "Selecciona el servicio que mejor describe tu situación.",
  back: "<- Volver",
  next: "Siguiente ->",
  loading: "Cargando catálogo de servicios...",
  loadError: "No se pudo cargar el catálogo de servicios. Inténtalo de nuevo en unos segundos.",
  fallbackCategoryDescription: "Servicios disponibles para este tipo de trámite.",
  categoryCountSuffix: "servicios",
  filterPlaceholder: "Busca tu trámite (ej. arraigo, estudios, nacionalidad...)",
  noResults: "No encontramos trámites para tu búsqueda. Prueba con otra palabra.",
};

const EN_COPY = {
  title: "Choose Your Procedure",
  subtitle: "Select the service that best matches your situation.",
  visadoTitle: "Choose your Visa",
  back: "<- Back",
  next: "Next ->",
  loading: "Loading service catalog...",
  loadError: "Service catalog could not be loaded. Please try again in a few seconds.",
  fallbackCategoryDescription: "Available services for this procedure category.",
  categoryCountSuffix: "services",
  filterPlaceholder: "Search your procedure (e.g. rooting, studies, nationality...)",
  noResults: "No procedures match your search. Try another keyword.",
};

function normalizeToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function findOptionByToken(options: ServiceOption[], value: string): ServiceOption | undefined {
  const token = normalizeToken(value);
  if (!token) return undefined;

  return options.find((option) => {
    const candidates = [
      option.optionId,
      option.serviceId,
      option.canonicalServiceId,
      option.selectionLabel,
      ...option.aliases,
      ...option.searchTokens,
    ];
    return candidates.some((candidate) => normalizeToken(candidate) === token);
  });
}

export default function EmpiezaTuTramiteClient({ locale = "es" }: { locale?: Locale }) {
  const { formData, updateFormData } = useForm();
  const router = useRouter();
  const copy = locale === "en" ? EN_COPY : ES_COPY;
  const [selectedOption, setSelectedOption] = useState("");
  const [categories, setCategories] = useState<ServiceOptionsCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [tipo, setTipo] = useState<string | null>(null);

  // Leemos ?tipo= del query sin useSearchParams (evita el Suspense boundary).
  useEffect(() => {
    if (typeof window !== "undefined") {
      setTipo(new URLSearchParams(window.location.search).get("tipo"));
    }
  }, []);

  const allOptions = useMemo(
    () => categories.flatMap((category) => category.options || []),
    [categories],
  );

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    async function loadCatalog() {
      setIsLoading(true);
      setLoadError("");
      try {
        const response = await fetch(`/api/services/options?locale=${locale}`, {
          method: "GET",
          signal: controller.signal,
          cache: "no-store",
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const payload = (await response.json()) as ServiceOptionsResponse;
        if (!active) return;

        setCategories(Array.isArray(payload.categories) ? payload.categories : []);
      } catch {
        if (!active || controller.signal.aborted) return;
        setCategories([]);
        setLoadError(copy.loadError);
      } finally {
        if (active) setIsLoading(false);
      }
    }

    loadCatalog();

    return () => {
      active = false;
      controller.abort();
    };
  }, [copy.loadError, locale]);

  useEffect(() => {
    if (allOptions.length === 0) return;

    if (!selectedOption && formData.serviceType) {
      const found = findOptionByToken(allOptions, formData.serviceType);
      if (found) {
        setSelectedOption(found.optionId);
      }
    }
  }, [allOptions, formData.serviceType, selectedOption]);

  // Filtro por tipo de flujo: por defecto se excluyen visados y renovaciones (tienen su
  // propio acceso); con ?tipo=visado se muestran SOLO los visados.
  const baseCategories = useMemo(() => {
    return categories
      .map((cat) => {
        const options = cat.options.filter((option) => {
          const hay = normalizeToken(
            [option.selectionLabel, cat.category, option.canonicalServiceId, option.serviceId, ...(option.searchTokens || [])].join(" "),
          );
          const isVisado = hay.includes("visa");
          const isRenovacion = hay.includes("renovac");
          if (tipo === "visado") return isVisado;
          return !isVisado && !isRenovacion;
        });
        return { ...cat, options, total: options.length };
      })
      .filter((cat) => cat.options.length > 0);
  }, [categories, tipo]);

  // Filtro dinámico: deja solo las categorías/opciones que casan con la búsqueda.
  const filteredCategories = useMemo(() => {
    const q = normalizeToken(query);
    if (!q) return baseCategories;
    const tokens = q.split("-").filter(Boolean);
    return baseCategories
      .map((cat) => {
        const options = cat.options.filter((option) => {
          const haystack = normalizeToken(
            [
              option.selectionLabel,
              option.description,
              cat.category,
              ...(option.searchTokens || []),
              ...(option.aliases || []),
            ].join(" "),
          );
          return tokens.every((token) => haystack.includes(token));
        });
        return { ...cat, options, total: options.length };
      })
      .filter((cat) => cat.options.length > 0);
  }, [baseCategories, query]);

  // Repartir las categorías en 3 columnas balanceadas por nº de servicios (greedy bin-packing).
  const columns = useMemo(() => {
    const cols: ServiceOptionsCategory[][] = [[], [], []];
    const sums = [0, 0, 0];
    for (const cat of filteredCategories) {
      const target = sums.indexOf(Math.min(...sums));
      cols[target].push(cat);
      sums[target] += cat.total || cat.options.length || 1;
    }
    return cols;
  }, [filteredCategories]);

  const renderCategory = (cat: ServiceOptionsCategory) => {
    const isHighlighted =
      normalizeToken(cat.category).includes("nomada") ||
      cat.options.some((option) => /nomada|beckham/.test(normalizeToken(option.selectionLabel)));

    return (
      <div key={cat.category}>
        {/* Cabecera de categoría */}
        <div className={`mb-2 px-3 py-2 rounded-lg ${isHighlighted ? 'bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/40' : 'bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/40'}`}>
          <div className="flex items-center justify-between flex-wrap gap-1">
            <span className={`text-sm font-bold ${isHighlighted ? 'text-amber-700 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {cat.category}
            </span>
            <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${isHighlighted ? 'bg-amber-100 dark:bg-amber-800/30 text-amber-600 dark:text-amber-300' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
              {cat.total} {copy.categoryCountSuffix}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{copy.fallbackCategoryDescription}</p>
        </div>

        {/* Servicios de la categoría */}
        <div className="space-y-2 pl-1">
          {cat.options.map((servicio) => (
            <div
              key={servicio.optionId}
              onClick={() => setSelectedOption(servicio.optionId)}
              className={`flex items-start text-sm font-medium p-3 rounded-lg border transition cursor-pointer ${
                selectedOption === servicio.optionId
                  ? isHighlighted
                    ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/10 text-amber-800 dark:text-amber-200'
                    : 'border-cream-400 dark:border-cream-500 bg-cream-50 dark:bg-cream-900/10 text-gray-800 dark:text-gray-100'
                  : 'border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } shadow-sm`}
            >
              <div className={`w-4 h-4 rounded-full border mr-3 flex-shrink-0 flex items-center justify-center mt-0.5 ${
                selectedOption === servicio.optionId
                  ? isHighlighted ? 'border-amber-500 bg-amber-500' : 'border-cream-500 bg-cream-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}>
                {selectedOption === servicio.optionId && <div className="w-2 h-2 bg-white rounded-full"></div>}
              </div>
              <div className="flex flex-col min-w-0">
                <span>{servicio.selectionLabel}</span>
                {servicio.description && (
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">
                    {servicio.description}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleNextStep = () => {
    if (!selectedOption) return;

    const selected = allOptions.find((option) => option.optionId === selectedOption);
    const canonicalValue =
      selected?.canonicalServiceId || selected?.serviceId || selected?.selectionLabel || selectedOption;

    updateFormData("serviceType", canonicalValue);
    router.push(localizePath(`/empieza-tramite?from=${encodeURIComponent(canonicalValue)}`, locale));
  };

  return (
    <main className="bg-white dark:bg-gray-900">
      <div className="relative flex">
        <div className="w-full max-w-5xl mx-auto">
          <div className="min-h-[100dvh] h-full flex flex-col after:flex-1">
            <div className="flex-1 pt-24">
              <OnboardingProgress step={1} totalSteps={3} />
            </div>

            <div className="px-4 py-8">
              <div className="max-w-5xl mx-auto">
                <h1 className="text-3xl text-gray-800 dark:text-gray-100 font-bold mb-2">{tipo === "visado" ? copy.visadoTitle : copy.title}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{copy.subtitle}</p>

                {/* Filtro dinámico de servicios */}
                <div className="relative mb-8">
                  <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={copy.filterPlaceholder}
                    className="w-full bg-slate-50 dark:bg-[#0b1120] border border-gray-300 dark:border-gray-700 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:border-amber-500 focus:ring-amber-500 dark:focus:border-amber-400 dark:focus:ring-amber-400 transition-all shadow-sm"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm"
                      aria-label="Limpiar búsqueda"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {isLoading && (
                  <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700/60 bg-gray-50 dark:bg-gray-800/40 p-3 text-sm text-gray-500 dark:text-gray-400">
                    {copy.loading}
                  </div>
                )}

                {!isLoading && loadError && (
                  <div className="mb-6 rounded-lg border border-red-200 dark:border-red-700/60 bg-red-50 dark:bg-red-900/10 p-3 text-sm text-red-600 dark:text-red-300">
                    {loadError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6 mb-8">
                  {columns.map((col, idx) => (
                    <div key={idx} className="space-y-6">
                      {col.map((cat) => renderCategory(cat))}
                    </div>
                  ))}
                </div>

                {!isLoading && !loadError && filteredCategories.length === 0 && (
                  <div className="mb-8 rounded-lg border border-gray-200 dark:border-gray-700/60 bg-gray-50 dark:bg-gray-800/40 p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                    {copy.noResults}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <Link
                    href={localizePath("/", locale)}
                    className="btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 text-gray-300 dark:text-gray-600 rounded-lg"
                  >
                    {copy.back}
                  </Link>

                  <button
                    onClick={handleNextStep}
                    disabled={!selectedOption}
                    className={`btn bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white ml-auto rounded-lg ${
                      !selectedOption ? 'opacity-50 pointer-events-none' : ''
                    }`}
                  >
                    {copy.next}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
